import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { config } from '@/shared/config';

import enAccess from './locales/en/access.json';
import enAuth from './locales/en/auth.json';
import enBilling from './locales/en/billing.json';
import enBooking from './locales/en/booking.json';
import enCommon from './locales/en/common.json';
import enErrors from './locales/en/errors.json';
import enMessages from './locales/en/messages.json';
import enRecords from './locales/en/records.json';
import enReports from './locales/en/reports.json';
import enSchedule from './locales/en/schedule.json';
import enSite from './locales/en/site.json';
import enUsers from './locales/en/users.json';
import enValidation from './locales/en/validation.json';
import faAccess from './locales/fa/access.json';
import faAuth from './locales/fa/auth.json';
import faBilling from './locales/fa/billing.json';
import faBooking from './locales/fa/booking.json';
import faCommon from './locales/fa/common.json';
import faErrors from './locales/fa/errors.json';
import faMessages from './locales/fa/messages.json';
import faRecords from './locales/fa/records.json';
import faReports from './locales/fa/reports.json';
import faSchedule from './locales/fa/schedule.json';
import faSite from './locales/fa/site.json';
import faUsers from './locales/fa/users.json';
import faValidation from './locales/fa/validation.json';

/**
 * Namespaces per feature (CLAUDE.md §9). A key present in one locale and
 * missing in the other fails CI — see `make check`, rule `i18n.key-parity`.
 */
export const resources = {
  fa: {
    common: faCommon,
    auth: faAuth,
    access: faAccess,
    users: faUsers,
    booking: faBooking,
    schedule: faSchedule,
    billing: faBilling,
    records: faRecords,
    messages: faMessages,
    reports: faReports,
    site: faSite,
    errors: faErrors,
    validation: faValidation,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    access: enAccess,
    users: enUsers,
    booking: enBooking,
    schedule: enSchedule,
    billing: enBilling,
    records: enRecords,
    messages: enMessages,
    reports: enReports,
    site: enSite,
    errors: enErrors,
    validation: enValidation,
  },
} as const;

/**
 * Where the chosen language is remembered. Written by the app's locale provider
 * on every change, read once here — a switch in the header that forgets itself
 * on reload is not a setting.
 */
export const LOCALE_STORAGE_KEY = 'clinic.locale';

/**
 * `?lang=en` on the address — how the landing page's language button hands a
 * patient to the booking app in their language. It wins over what is stored,
 * and the locale provider then remembers it like any other choice.
 */
function requestedLocale(): string | null {
  try {
    const requested = new URLSearchParams(window.location.search).get('lang');

    return requested !== null && (config.supportedLocales as readonly string[]).includes(requested)
      ? requested
      : null;
  } catch {
    return null;
  }
}

function storedLocale(): string {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);

    return stored !== null && (config.supportedLocales as readonly string[]).includes(stored)
      ? stored
      : config.defaultLocale;
  } catch {
    // private mode or blocked storage: the default is always a valid answer
    return config.defaultLocale;
  }
}

void i18next.use(initReactI18next).init({
  resources,
  lng: requestedLocale() ?? storedLocale(),
  fallbackLng: 'en',
  supportedLngs: config.supportedLocales,
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  returnNull: false,
});

export const i18n = i18next;
